import { createReducer } from 'reduxsauce';
import { AnyAction } from 'redux';
import EndorsementDto from 'Models/Endorsement/Dto/EndorsementDto';
import UpdateEndorsementDto from 'Models/Endorsement/Dto/UpdateEndorsementDto';
import ApiError from 'Models/Other/ApiError';
import StateReadonly from './StateModel';
import { createScopedActions } from '.';

/* ------------- Interfaces for ReduxSauce ------------- */
interface EndorsementState {
   endorsementHistory: readonly EndorsementDto[];
   activeEndorsements: readonly EndorsementDto[];
   updateEndorsement: EndorsementDto | null;
   updateEndorsementError: ApiError | null;
}

export type EndorsementStateReadonly = Readonly<EndorsementState>;

interface TypeNames {
   GET_ENDORSEMENT_HISTORY_BY_ID_REQUEST: string;
   GET_ENDORSEMENT_HISTORY_BY_ID_SUCCESS: string;
   GET_ENDORSEMENT_HISTORY_BY_ID_FAILURE: string;
   GET_ENDORSEMENT_HISTORY_BY_ID_RESET: string;

   GET_ACTIVE_ENDORSEMENTS_BY_CONTACT_ID_REQUEST: string;
   GET_ACTIVE_ENDORSEMENTS_BY_CONTACT_ID_SUCCESS: string;
   GET_ACTIVE_ENDORSEMENTS_BY_CONTACT_ID_FAILURE: string;

   UPDATE_ENDORSEMENT_REQUEST: string;
   UPDATE_ENDORSEMENT_SUCCESS: string;
   UPDATE_ENDORSEMENT_FAILURE: string;
   UPDATE_ENDORSEMENT_RESET: string;
}

type EndorsementTypeNames = Readonly<TypeNames>;

export interface EndorsementCreators {
   getEndorsementHistoryByIdRequest: (id: string) => AnyAction;
   getEndorsementHistoryByIdSuccess: (data: EndorsementDto[] | undefined) => AnyAction;
   getEndorsementHistoryByIdFailure: (error: ApiError) => AnyAction;
   getEndorsementHistoryByIdReset: () => AnyAction;

   getActiveEndorsementsByContactIdRequest: (contactId: string) => AnyAction;
   getActiveEndorsementsByContactIdSuccess: (data: EndorsementDto[] | undefined) => AnyAction;
   getActiveEndorsementsByContactIdFailure: (error: ApiError) => AnyAction;

   updateEndorsementRequest: (updateData: UpdateEndorsementDto) => AnyAction;
   updateEndorsementSuccess: (data: EndorsementDto | undefined) => AnyAction;
   updateEndorsementFailure: (error: ApiError) => AnyAction;
   updateEndorsementReset: () => AnyAction;
}

/* ------------- Initial State ------------- */
export const INITIAL_STATE: EndorsementStateReadonly = {
   endorsementHistory: [],
   activeEndorsements: [],
   updateEndorsement: null,
   updateEndorsementError: null,
};

/* ------------- Reducers ------------- */
const getEndorsementHistoryByIdSuccess = (
   state: EndorsementStateReadonly,
   action: AnyAction
): EndorsementStateReadonly => {
   return { ...state, endorsementHistory: action.data };
};

const getEndorsementHistoryByIdReset = (
   state: EndorsementStateReadonly
): EndorsementStateReadonly => {
   return { ...state, endorsementHistory: [] };
};

const getActiveEndorsementsByContactIdSuccess = (
   state: EndorsementStateReadonly,
   action: AnyAction
): EndorsementStateReadonly => {
   return { ...state, activeEndorsements: action.data };
};

const updateEndorsementSuccess = (
   state: EndorsementStateReadonly,
   action: AnyAction
): EndorsementStateReadonly => {
   return { ...state, updateEndorsement: action.data };
};

const updateEndorsementFailure = (
   state: EndorsementStateReadonly,
   action: AnyAction
): EndorsementStateReadonly => {
   return { ...state, updateEndorsementError: action.error };
};

const updateEndorsementReset = (
   state: EndorsementStateReadonly
): EndorsementStateReadonly => {
   return { ...state, updateEndorsement: null, updateEndorsementError: null };
};

/* ------------- Types and Action Creators ------------- */
const { Types, Creators } = createScopedActions<
   EndorsementTypeNames,
   EndorsementCreators
>('endorsement', {
   getEndorsementHistoryByIdRequest: ['id'],
   getEndorsementHistoryByIdSuccess: ['data'],
   getEndorsementHistoryByIdFailure: ['error'],
   getEndorsementHistoryByIdReset: [],

   getActiveEndorsementsByContactIdRequest: ['contactId'],
   getActiveEndorsementsByContactIdSuccess: ['data'],
   getActiveEndorsementsByContactIdFailure: ['error'],

   updateEndorsementRequest: ['updateData'],
   updateEndorsementSuccess: ['data'],
   updateEndorsementFailure: ['error'],
   updateEndorsementReset: [],
});

export const EndorsementTypes = Types;
export const EndorsementActions = Creators;

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
   [Types.GET_ENDORSEMENT_HISTORY_BY_ID_SUCCESS]: getEndorsementHistoryByIdSuccess,
   [Types.GET_ENDORSEMENT_HISTORY_BY_ID_RESET]: getEndorsementHistoryByIdReset,
   [Types.GET_ACTIVE_ENDORSEMENTS_BY_CONTACT_ID_SUCCESS]: getActiveEndorsementsByContactIdSuccess,
   [Types.UPDATE_ENDORSEMENT_SUCCESS]: updateEndorsementSuccess,
   [Types.UPDATE_ENDORSEMENT_FAILURE]: updateEndorsementFailure,
   [Types.UPDATE_ENDORSEMENT_RESET]: updateEndorsementReset,
});

/* ------------- Selectors ------------- */
export const selectEndorsementHistory = (
   state: StateReadonly
): readonly EndorsementDto[] => state.endorsement.endorsementHistory;

export const selectActiveEndorsements = (
   state: StateReadonly
): readonly EndorsementDto[] => state.endorsement.activeEndorsements;

export const selectUpdateEndorsement = (
   state: StateReadonly
): EndorsementDto | null => state.endorsement.updateEndorsement;

export const selectUpdateEndorsementError = (
   state: StateReadonly
): ApiError | null => state.endorsement.updateEndorsementError;
